import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { ROLES } from "@/data/content";

export default function ProfessionalServices() {
  useSEO(
    "Professional Services | Business Analysts, Project, Data & IT | Synergy Outsourcing",
    "Add experienced Business Analysts, Project Managers, Data Analysts, Finance professionals, IT specialists and other specialists to your team through a managed global delivery model."
  );
  return (
    <>
      <PageHero
        eyebrow="Professional Services"
        lines={["Professional talent without", "the full-time", "overhead."]}
        copy="Add experienced Business Analysts, Project Managers, Data Analysts, Finance professionals and other specialists to your team through a managed global delivery model."
      >
        <Link
          to="/get-your-team"
          data-testid="pro-services-hero-cta"
          className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
        >
          Find Your Next Professional
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </PageHero>

      <section className="py-14 sm:py-16" data-testid="pro-services-roles">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            {ROLES.map((r, i) => (
              <Reveal key={r.name} delay={(i % 2) * 0.1}>
                <article
                  data-testid={`role-detail-${r.name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                  className="card-hover h-full rounded-2xl border border-white/10 bg-ink-2 p-8"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft text-brand">
                      <r.icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <div>
                      <h2 className="font-display text-xl font-semibold text-white">{r.name}</h2>
                      <p className="text-sm text-mist">{r.tagline}</p>
                    </div>
                  </div>
                  <div className="mt-7 grid gap-6 sm:grid-cols-3">
                    <div>
                      <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">Responsibilities</h3>
                      <ul className="mt-3 space-y-2">
                        {r.responsibilities.map((x) => (
                          <li key={x} className="flex items-start gap-2 text-xs leading-relaxed text-slate-300">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" />
                            {x}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">Skills</h3>
                      <ul className="mt-3 space-y-2">
                        {r.skills.map((x) => (
                          <li key={x} className="flex items-start gap-2 text-xs leading-relaxed text-slate-300">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" />
                            {x}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">Deliverables</h3>
                      <ul className="mt-3 space-y-2">
                        {r.deliverables.map((x) => (
                          <li key={x} className="flex items-start gap-2 text-xs leading-relaxed text-slate-300">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" />
                            {x}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-7 space-y-3 border-t border-white/10 pt-5">
                    <p className="text-sm leading-relaxed text-mist">
                      <span className="font-semibold text-slate-200">Suited for: </span>
                      {r.suited}
                    </p>
                    <p className="text-sm leading-relaxed text-mist">
                      <span className="font-semibold text-slate-200">How Synergy Outsourcing supports it: </span>
                      {r.support}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTASection
        title="Find your next professional."
        copy="Tell us the capability you need. We'll define the role, shortlist pre-screened professionals and manage the engagement from day one."
      />
    </>
  );
}
